import { updateTotal } from './utils.js';
import { getApiUrl, fetchApiData, updateQueryString, getQueryParam } from '../shared/utils.js';
import { pgQueryParam, tabQueryParam, displayDateFormat, serverDateTimeSecondsFormat, addClass, removeClass, hiddenClass } from '../../../config.js';
import { elements } from './elements.js';
import { highlightTabLink, updateTabCounts, initializeTabs } from './tabs.js';
import { renderPagination } from '../shared/pagination.js';
import { format, parse } from 'date-fns';
import { formDataParsing } from './calendar.js';

const onPageLinkClick = (page) => {
    const selectedTab = getQueryParam(tabQueryParam) || 'pages';
    updateQueryString({ [pgQueryParam]: page, [tabQueryParam]: selectedTab });
    fetchResults();
};

const appendSearchResults = (entries, templateId) => {
    const { resultsContainer, noResultsContainer } = elements;
    if (!resultsContainer || !noResultsContainer) return;

    if (!entries.length) {
        addClass(resultsContainer, hiddenClass);
        removeClass(noResultsContainer, hiddenClass);
        return;
    }

    addClass(noResultsContainer, hiddenClass);
    removeClass(resultsContainer, hiddenClass);
    resultsContainer.innerHTML = '';

    const ul = document.createElement('ul');
    ul.classList.add('space-y-5');
    const templateBase = document.getElementById(templateId)?.content;

    entries.forEach(entry => {
        const template = templateBase.cloneNode(true);
        if (!template) return;

        const item = template.querySelector('.js-result-template');
        if (!item) return;

        const titleLink = item.querySelector('.js-result-template-link');
        if (titleLink) {
            titleLink.href = entry.url;
            titleLink.textContent = entry.title;
        }

        const teaserText = item.querySelector('.js-result-template-teaser');
        if (teaserText) {
            teaserText.innerHTML = entry.teaserText;
        }

        const dateUpdated = item.querySelector('.js-result-template-date');
        if (dateUpdated) {
            const dateParsed = parse(entry.dateUpdated, serverDateTimeSecondsFormat, new Date());
            dateUpdated.textContent = 'Updated: ' + format(dateParsed, displayDateFormat);
        }

        const resourceTypeTagsContainer = item.querySelector('.js-result-template-resource-types-tags');

        if (resourceTypeTagsContainer) {
            const tagTemplateClasses = resourceTypeTagsContainer.querySelector('.js-result-template-resource-tag').classList;

            resourceTypeTagsContainer.innerHTML = ''; // Clear existing content

            const appendTag = (tag) => {
                const span = document.createElement('span');
                span.classList = tagTemplateClasses;
                span.textContent = tag.title;
                resourceTypeTagsContainer.appendChild(span);
                return span;
            }

            if (entry.resourceTypes.length) {
                const firstTag = entry.resourceTypes.shift();
                const span = appendTag(firstTag);
                span.style.color = firstTag.colour;
                span.style.borderColor = firstTag.colour;
                item.style.borderColor = firstTag.colour;
            }

            entry.resourceTypes.forEach(appendTag);
        }

        ul.appendChild(item);
    });

    resultsContainer.appendChild(ul);
};

const handleSearchResponse = (response, searchForm, selectedTab, changeTabIfEmpty) => {
    if (!searchForm) return;

    // If no results for the current section and there is more than one page, find the next section with results
    if (changeTabIfEmpty && !response.data.length && Object.keys(response.meta.counts).length > 0) {
        const sections = Object.keys(response.meta.counts);
        const currentIndex = sections.indexOf(selectedTab);

        for (let i = 0; i < sections.length; i++) {
            if (i === currentIndex) continue; // Skip the current section
            if (response.meta.counts[sections[i]] > 0) {
                const nextSection = sections[i];
                updateQueryString({ [tabQueryParam]: nextSection, [pgQueryParam]: 1 });
                fetchResults(); // Requery for the next section with results
                return;
            }
        }
    }

    const templateId = searchForm.getAttribute('data-result-type') === 'resourceLibrary' ? 'resource-library-item-template' : 'search-result-template';

    // Update tab links
    updateTabCounts(response.meta.counts);
    updateTotal(response.meta.counts);
    highlightTabLink(selectedTab);

    appendSearchResults(
        response.data,
        templateId
    );

    const paginationContainer = document.querySelector('.js-pagination');
    if (paginationContainer) {
        renderPagination(response.meta.pagination, (page) => onPageLinkClick(page));
    }

    initializeTabs();
};

export const fetchResults = (changeTabIfEmpty = true) => {
    const { searchForm, resultsContainer } = elements;

    if (!searchForm || !resultsContainer) return;

    // Loading skeletons
    removeClass(elements.loadingSkeletons, hiddenClass);

    resultsContainer.innerHTML = '';

    const selectedTab = getQueryParam(tabQueryParam) || 'pages';
    const page = getQueryParam(pgQueryParam) || '1';

    let resultType;
    if (searchForm.getAttribute('data-result-type') === 'resourceLibrary') {
        const selectedFilter = document.querySelector('.js-search-type:checked')?.value;
        resultType = selectedFilter === 'corporate-documents' ? 'search/resources/corporate-documents' : 'search/resources';
    } else {
        resultType = 'search';
    }

    const url = getApiUrl(resultType, searchForm, formDataParsing);
    url.searchParams.set(pgQueryParam, page);
    url.searchParams.set(tabQueryParam, selectedTab);

    fetchApiData(
        url.toString(),
        (response) => {
            addClass(elements.loadingSkeletons, hiddenClass);
            handleSearchResponse(response, searchForm, selectedTab, changeTabIfEmpty);
        },
        () => {
            addClass(elements.loadingSkeletons, hiddenClass);
            if (resultsContainer) {
                resultsContainer.innerHTML = '<p class="text-error">Error loading search results. Please try again.</p>';
            }
        }
    );
};
